function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useContext } from 'react';
import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import DateRangePanel from './Panel/DateRangePanel';
import SingleSelectPanel from './Panel/SingleSelectPanel';
import DatepickerTrigger from './Trigger';
import { PLACEMENT_TOP_LEFT, PLACEMENT_TOP_RIGHT, PLACEMENT_BOTTOM_RIGHT, PLACEMENT_BOTTOM_LEFT, TAB_KEYCODE, ESCAPE_KEYCODE } from '../../Utils/constant';
import { DatepickerContext } from '../context';
import styles from '@dtsl/css-datepicker/dist/index.module.css';
function Datepicker(props) {
  const {
    trigger = true,
    triggerLabel = '',
    primaryAction = {
      label: 'Confirm',
      variant: 'primary',
      onClick: () => {},
      disabled: false
    },
    arrowClassName = '',
    onTriggerSelect = () => {},
    triggerClassName = '',
    secondaryAction = {
      label: 'Clear',
      variant: 'tertiary',
      onClick: () => {},
      disabled: false
    },
    triggerStyles = {},
    triggerContainerStyles = {},
    className,
    hideBorder,
    disableFloatingUI
  } = props;
  const {
    isOpen,
    handleOnClose,
    isRangeDatePicker,
    handleResetClick,
    setDisableConfirmButton,
    disabled,
    floatingUiProps,
    uniqueId,
    datepickerTriggerRef,
    datepickerContainerRef,
    placement = 'bottomLeft'
  } = useContext(DatepickerContext);
  const {
    floatingStyles,
    refs,
    context
  } = floatingUiProps;
  const datePickerContainerClass = classNames({
    [styles['sib-datepicker-container']]: true,
    [styles['sib-datepicker-container__new']]: true,
    [styles['sib-datepicker-container-disabled']]: disabled,
    [styles['is-open']]: isOpen,
    [className]: !!className
  });
  const handleKeyPress = event => {
    if (event.keyCode === TAB_KEYCODE) {
      const focusedItem = datepickerContainerRef?.current?.querySelectorAll('a, button:not([disabled]), input'); // tabout form the last focused element on the datepicker panel
      if (!event.shiftKey && focusedItem && event.target === focusedItem[focusedItem.length - 1]) {
        handleResetClick();
        handleOnClose(event);
        datepickerTriggerRef.current.focus();
      }
    }
    if (event.keyCode === ESCAPE_KEYCODE) {
      handleResetClick();
      handleOnClose(event);
    }
  };
  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);
  const clearAction = {
    ...secondaryAction,
    onClick: e => {
      handleResetClick();
      secondaryAction.onClick(e);
    }
  };
  const confirmAction = {
    ...primaryAction,
    onClick: e => {
      handleOnClose(e);
      setDisableConfirmButton(true);
      primaryAction.onClick(e);
    }
  };
  const datepickerPlacementClasses = classNames({
    [styles['sib-datepickers-panel']]: isOpen,
    [styles['sib-datepicker--no-border']]: !!hideBorder,
    [PLACEMENT_TOP_LEFT]: placement === 'topLeft',
    [PLACEMENT_TOP_RIGHT]: placement === 'topRight',
    [PLACEMENT_BOTTOM_RIGHT]: placement === 'bottomRight',
    [PLACEMENT_BOTTOM_LEFT]: placement === 'bottomLeft'
  });
  const panelContent = isRangeDatePicker ? /*#__PURE__*/React.createElement(DateRangePanel, _extends({}, props, {
    clearAction: clearAction,
    confirmAction: confirmAction
  })) : /*#__PURE__*/React.createElement(SingleSelectPanel, _extends({}, props, {
    clearAction: clearAction,
    confirmAction: confirmAction
  }));
  return /*#__PURE__*/React.createElement("div", {
    className: datePickerContainerClass
  }, trigger && /*#__PURE__*/React.createElement(DatepickerTrigger, {
    triggerLabel: triggerLabel,
    triggerClassName: triggerClassName,
    arrowClassName: arrowClassName,
    onTriggerSelect: onTriggerSelect,
    triggerStyles: triggerStyles,
    triggerContainerStyles: triggerContainerStyles,
    disabled: disabled,
    dataTarget: `sibDatepickerPanel--${uniqueId}`,
    disableFloatingUI: disableFloatingUI
  }), disableFloatingUI ? /*#__PURE__*/React.createElement("div", {
    id: `sibDatepickerPanel--${uniqueId}`
  }, /*#__PURE__*/React.createElement("div", {
    className: datepickerPlacementClasses,
    ref: datepickerContainerRef,
    id: "sib-datepickers-id"
  }, panelContent)) : /*#__PURE__*/React.createElement(FloatingPortal, null, /*#__PURE__*/React.createElement(FloatingFocusManager, {
    context: context,
    initialFocus: -1,
    visuallyHiddenDismiss: true,
    returnFocus: false
  }, isOpen ? /*#__PURE__*/React.createElement("div", {
    id: `sibDatepickerPanel--${uniqueId}`,
    ref: refs.setFloating,
    style: {
      ...floatingStyles
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: datepickerPlacementClasses,
    ref: datepickerContainerRef,
    id: "sib-datepickers-id"
  }, panelContent)) : null)));
}
Datepicker.propTypes = {
  trigger: PropTypes.bool,
  triggerLabel: PropTypes.string,
  arrowClassName: PropTypes.string,
  onTriggerSelect: PropTypes.func,
  triggerClassName: PropTypes.string,
  triggerStyles: PropTypes.object,
  triggerContainerStyles: PropTypes.object,
  className: PropTypes.string,
  secondaryAction: PropTypes.shape({
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'destructive']),
    label: PropTypes.string,
    onClick: PropTypes.func,
    iconAlone: PropTypes.bool,
    icon: PropTypes.node,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    className: PropTypes.string
  }),
  primaryAction: PropTypes.shape({
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'destructive']),
    label: PropTypes.string,
    onClick: PropTypes.func,
    iconAlone: PropTypes.bool,
    icon: PropTypes.node,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    className: PropTypes.string,
    handleResetClick: PropTypes.func
  }),
  disabled: PropTypes.bool,
  hideBorder: PropTypes.bool,
  placement: PropTypes.oneOf(['top', 'topLeft', 'topRight', 'bottom', 'bottomLeft', 'bottomRight']),
  disableFloatingUI: PropTypes.bool
};
export default Datepicker;